import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import html2canvas from "html2canvas";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

const storage = getStorage();

function formatDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

async function forceDownload(url, filename = "capture.png") {
  try {
    const res = await fetch(url, { credentials: "omit" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    window.open(url, "_blank", "noopener");
  }
}

export function BubblePostsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);

  // Editor fields (modal)
  const [message, setMessage] = useState("");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [color, setColor] = useState("#000000");
  const [bubblePos, setBubblePos] = useState({ left: 170, top: 10 });

  // Queue
  const [tailSide, setTailSide] = useState("bottom");

  // Refs modal
  const containerRef = useRef(null);
  const bubbleBoxRef = useRef(null);
  const editorRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    const run = async () => {
      try {
        const qy = query(collection(db, "bubble_posts"), orderBy("created_at", "desc"));
        const snap = await getDocs(qy);
        setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  // ---------- styles réutilisables (classes + CSS global responsive)
  const css = `
  .bp-wrap { max-width: 1200px; margin: 0 auto; padding: 16px; }
  .bp-header { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; }
  .bp-header h2 { margin: 0; font-size: clamp(18px, 2vw, 22px); }
  .bp-btn {
    display:inline-flex; align-items:center; justify-content:center;
    min-height:44px; padding:10px 12px; font-size:14px; border:none; border-radius:10px;
    cursor:pointer; text-decoration:none;
  }
  .bp-btn--primary { background:#128C7E; color:#fff; }
  .bp-btn--secondary { background:#e5e7eb; color:#111827; }
  .bp-btn--block { width:100%; }

  .bp-empty {
    padding: 24px;
    border: 1px dashed #d1d5db;
    border-radius: 12px;
    background: #f9fafb;
    text-align:center;
  }

  .bp-grid {
    display:grid;
    gap: clamp(12px, 2vw, 18px);
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .bp-card {
    background:#fff; border:1px solid #e5e7eb; border-radius:14px; overflow:hidden;
    box-shadow: 0 1px 0 rgba(0,0,0,0.02);
    display:flex; flex-direction:column;
    transition: transform .12s ease, box-shadow .12s ease;
  }
  .bp-card:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(0,0,0,0.06); }

  .bp-thumb {
    width:100%;
    aspect-ratio: 16/9; /* garde la forme responsive */
    background:#f3f4f6;
  }
  .bp-thumb img { width:100%; height:100%; object-fit:cover; display:block; }

  .bp-card-body { padding: 12px; display:flex; flex-direction:column; gap:8px; }
  .bp-meta { display:flex; gap:8px; flex-wrap:wrap; color:#6b7280; font-size:12px; }
  .bp-text { font-size:13px; color:#374151; max-height: 60px; overflow: hidden; }

  .bp-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top: 6px; }
  @media (max-width: 480px) {
    .bp-actions .bp-btn { flex: 1 1 calc(50% - 8px); } /* 2 par ligne sur mobile */
  }

  .bp-skeleton {
    border:1px solid #e5e7eb; border-radius:12px; padding:12px;
  }
  .bp-skel-bar { background:#f3f4f6; border-radius:8px; }
  .bp-skel-thumb { width:100%; height:160px; }
  .bp-skel-60 { width:60%; height:12px; margin-top:12px;}
  .bp-skel-40 { width:40%; height:12px; margin-top:8px;}
  .bp-skel-row { display:flex; gap:8px; margin-top:12px;}
  .bp-skel-btn { width:100px; height:36px; }

  /* Modal responsive */
  .bp-backdrop {
    position:fixed; inset:0; background:rgba(0,0,0,0.45);
    display:flex; align-items:center; justify-content:center; padding:16px; z-index:999;
  }
  .bp-modal {
    width:min(980px, 100%);
    background:#fff; border-radius:12px; padding:16px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.25);
    max-height: 90vh; overflow:auto;
  }
  .bp-toolbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom: 10px; }

  /* Zone de composition responsive */
 .bp-canvas {
  position: relative;
  display: flex;
  align-items: center;        /* centre vertical */
  justify-content: center;    /* centre horizontal */
  gap: 10px;
  margin: 0 auto;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  width: clamp(280px, 90vw, 560px);
  aspect-ratio: 1 / 1;        /* carré à l’écran (cohérent si tu veux un export carré) */
  padding: 20px;
}

.bp-avatar {
  width: clamp(140px, 60%, 300px);
  height: clamp(140px, 60%, 300px);
  border-radius: 50%;
  object-fit: cover;
  /* on supprime le positionnement absolu pour autoriser le centrage flex */
  /* supprime: position:absolute; bottom:20px; left:20px; */
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
}


  /* Bubble container */
  .bp-bubble {
    position:absolute; max-width:min(360px, 70%); min-height:50px;
    display:inline-block; white-space:pre-wrap; word-break:break-word; line-height:1.2;
    padding:10px 15px; background:#fff; color:#000; border:2px solid rgba(0,0,0,0.3);
    border-radius:20px; box-shadow: 4px 4px 10px rgba(0,0,0,0.2); text-align:justify;
    cursor:grab; outline:none; user-select:text; overflow:visible;
  }

  textarea.bp-input {
    width:100%; padding:10px; border-radius:8px; border:1px solid #d1d5db; resize:vertical;
    font-size:14px;
  }
  `;

  const bubbleStyle = useMemo(() => ({
    top: bubblePos.top,
    left: bubblePos.left,
    fontFamily,
    color,
  }), [bubblePos, fontFamily, color]);

  // ---------- queue dynamique
  const computeTailSide = () => {
    const bubbleBox = bubbleBoxRef.current;
    const img = imgRef.current;
    if (!bubbleBox || !img) return;

    const b = bubbleBox.getBoundingClientRect();
    const i = img.getBoundingClientRect();

    if (b.right < i.left) return setTailSide("right");
    if (b.left > i.right) return setTailSide("left");
    if (b.bottom < i.top) return setTailSide("bottom");
    if (b.top > i.bottom) return setTailSide("top");
    setTailSide("bottom");
  };

  const getTailLayers = () => {
    const base = { position: "absolute", width: 0, height: 0, pointerEvents: "none" };
    const offset = 20;

    switch (tailSide) {
      case "left":
        return {
          under: { ...base, left: -17, top: offset, borderTop: "17px solid transparent", borderBottom: "17px solid transparent", borderRight: "17px solid rgba(0,0,0,0.3)" },
          top:   { ...base, left: -15, top: offset, borderTop: "15px solid transparent", borderBottom: "15px solid transparent", borderRight: "15px solid white" },
        };
      case "right":
        return {
          under: { ...base, right: -17, top: offset, borderTop: "17px solid transparent", borderBottom: "17px solid transparent", borderLeft: "17px solid rgba(0,0,0,0.3)" },
          top:   { ...base, right: -15, top: offset, borderTop: "15px solid transparent", borderBottom: "15px solid transparent", borderLeft: "15px solid white" },
        };
      case "top":
        return {
          under: { ...base, top: -17, left: offset, borderLeft: "17px solid transparent", borderRight: "17px solid transparent", borderBottom: "17px solid rgba(0,0,0,0.3)" },
          top:   { ...base, top: -15, left: offset, borderLeft: "15px solid transparent", borderRight: "15px solid transparent", borderBottom: "15px solid white" },
        };
      case "bottom":
      default:
        return {
          under: { ...base, bottom: -17, left: offset, borderLeft: "17px solid transparent", borderRight: "17px solid transparent", borderTop: "17px solid rgba(0,0,0,0.3)" },
          top:   { ...base, bottom: -15, left: offset, borderLeft: "15px solid transparent", borderRight: "15px solid transparent", borderTop: "15px solid white" },
        };
    }
  };

  // ---------- modal open/close
  const onOpenEdit = (docData) => {
    setCurrent(docData);
    setMessage(docData.message_html || docData.message_plain || "Écris ici…");
    setFontFamily(docData.font_family || "Arial");
    setColor(docData.color || "#000000");
    setBubblePos({
      left: typeof docData.bubble_left === "number" ? docData.bubble_left : 170,
      top: typeof docData.bubble_top === "number" ? docData.bubble_top : 10,
    });
    setOpen(true);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = (docData.message_html || "").trim() || "Écris ici…";
      }
      computeTailSide();
    }, 0);
  };

  const onClose = () => {
    setOpen(false);
    setCurrent(null);
  };

  // ---------- rich text
  const applyExec = (cmd, val = null) => {
    editorRef.current?.focus();
    try { document.execCommand("styleWithCSS", true); } catch (_) {}
    document.execCommand(cmd, false, val);
  };

  const onColorChange = (e) => {
    const c = e.target.value;
    setColor(c);
    applyExec("foreColor", c);
  };

  const onFontChange = (e) => {
    const f = e.target.value;
    setFontFamily(f);
    applyExec("fontName", f);
  };

  const onBubbleInput = () => {
    setMessage(editorRef.current?.innerHTML || "");
  };

  const onTextareaChange = (e) => {
    const val = e.target.value;
    setMessage(val);
    if (editorRef.current) {
      editorRef.current.innerHTML = val.replace(/\n/g, "<br>");
    }
  };

  // ---------- drag bulle (rAF)
  useEffect(() => {
    if (!open) return;
    const bubble = bubbleBoxRef.current;
    const container = containerRef.current;
    if (!bubble || !container) return;

    let startX = 0, startY = 0, originLeft = 0, originTop = 0;
    let dragging = false;
    let rafId = 0;
    let nextLeft = 0, nextTop = 0;

    const tick = () => {
      bubble.style.cursor = "grabbing";
      setBubblePos(prev => {
        const clamped = {
          left: Math.max(0, Math.min(nextLeft, container.clientWidth - bubble.clientWidth)),
          top: Math.max(0, Math.min(nextTop, container.clientHeight - bubble.clientHeight)),
        };
        return clamped.left === prev.left && clamped.top === prev.top ? prev : clamped;
      });
      computeTailSide();
      if (dragging) rafId = requestAnimationFrame(tick);
    };

    const onPointerDown = (e) => {
      dragging = true;
      bubble.setPointerCapture(e.pointerId);
      startX = e.clientX; startY = e.clientY;
      originLeft = bubble.offsetLeft; originTop = bubble.offsetTop;
      nextLeft = originLeft; nextTop = originTop;
      rafId = requestAnimationFrame(tick);
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      nextLeft = originLeft + dx;
      nextTop = originTop + dy;
    };

    const onPointerUp = (e) => {
      dragging = false;
      bubble.releasePointerCapture(e.pointerId);
      cancelAnimationFrame(rafId);
      bubble.style.cursor = "grab";
      computeTailSide();
    };

    bubble.addEventListener("pointerdown", onPointerDown);
    bubble.addEventListener("pointermove", onPointerMove);
    bubble.addEventListener("pointerup", onPointerUp);
    return () => {
      bubble.removeEventListener("pointerdown", onPointerDown);
      bubble.removeEventListener("pointermove", onPointerMove);
      bubble.removeEventListener("pointerup", onPointerUp);
      cancelAnimationFrame(rafId);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(computeTailSide, 0);
    return () => clearTimeout(id);
  }, [open, message, fontFamily]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => computeTailSide();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  // ---------- capture
  const capture = async (side = 500) => {
  if (!containerRef.current) return null;
  setCapturing(true);
  try {
    // s'assurer que l'image est prête
    if (imgRef.current) {
      try {
        if (!imgRef.current.complete) await imgRef.current.decode();
      } catch {
        await new Promise((r) => {
          if (imgRef.current.complete) return r();
          imgRef.current.addEventListener("load", r, { once: true });
          imgRef.current.addEventListener("error", r, { once: true });
        });
      }
    }

    // laisser le layout se stabiliser
    await new Promise((r) => requestAnimationFrame(r));

    // cloner et forcer le format carré
    const src = containerRef.current;
    const clone = src.cloneNode(true);
    Object.assign(clone.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: `${side}px`,
      height: `${side}px`,
      background: "white",
      overflow: "visible",
      padding: getComputedStyle(src).padding,
    });
    document.body.appendChild(clone);

    const canvas = await html2canvas(clone, {
      backgroundColor: null,
      scale: 2,
      width: side,
      height: side,
      useCORS: true,
      allowTaint: false,
    });

    document.body.removeChild(clone);
    return canvas;
  } finally {
    setCapturing(false);
  }
};


  const onCaptureDownload = async () => {
    const canvas = await capture();
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `capture_${current?.id || Date.now()}.png`;
    link.click();
  };

  const onSave = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const canvas = await capture();
      if (!canvas) throw new Error("Capture échouée");
      const pngBlob = dataURLToBlob(canvas.toDataURL("image/png"));

      const path = `bubble_posts/${current.id}_${Date.now()}_composition.png`;
      const compRef = storageRef(storage, path);
      await uploadBytes(compRef, pngBlob);
      const newCompositionUrl = await getDownloadURL(compRef);

      const docRef = doc(db, "bubble_posts", current.id);
      await updateDoc(docRef, {
        message_html: editorRef.current?.innerHTML || "",
        message_plain: editorRef.current?.innerText || "",
        font_family: fontFamily,
        color,
        bubble_left: bubblePos.left,
        bubble_top: bubblePos.top,
        composition_url: newCompositionUrl,
        updated_at: new Date(),
      });

      const qy = query(collection(db, "bubble_posts"), orderBy("created_at", "desc"));
      const snap = await getDocs(qy);
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));

      setOpen(false);
      setCurrent(null);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async (item) => {
    if (open && current?.id === item.id) {
      computeTailSide();
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));
      const canvas = await capture();
      if (!canvas) return;
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `bubble_${item.id}.png`;
      a.click();
      return;
    }
    if (item.composition_url) {
      await forceDownload(item.composition_url, `bubble_${item.id}.png`);
    }
  };

  const dataURLToBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length; const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  // ---------- UI
  if (loading) {
    return (
      <div className="bp-wrap">
        <style>{css}</style>
        <h2 style={{ marginBottom: 12 }}>Mes créations</h2>
        <div className="bp-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bp-skeleton">
              <div className="bp-skel-bar bp-skel-thumb" />
              <div className="bp-skel-bar bp-skel-60" />
              <div className="bp-skel-bar bp-skel-40" />
              <div className="bp-skel-row">
                <div className="bp-skel-bar bp-skel-btn" />
                <div className="bp-skel-bar bp-skel-btn" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="bp-wrap">
        <div className="bp-header">
          <h2>Mes créations</h2>
          <a href="/bubble-editor" className="bp-btn bp-btn--primary">+ Nouvelle création</a>
        </div>

        {!items.length && (
          <div className="bp-empty">
            Aucun élément. Crée ta première composition depuis l’onglet “Éditeur”.
          </div>
        )}

        <div className="bp-grid">
          {items.map(it => (
            <article key={it.id} className="bp-card">
              <div className="bp-thumb">
                {it.composition_url ? (
                  <img src={it.composition_url} alt="capture" />
                ) : null}
              </div>
              <div className="bp-card-body">
                <div className="bp-meta">
                  <span>ID: {it.id}</span>
                  <span>•</span>
                  <span>Créé: {formatDate(it.created_at)}</span>
                </div>

                <div
                  className="bp-text"
                  dangerouslySetInnerHTML={{ __html: it.message_html || "" }}
                />

                <div className="bp-actions">
                  {it.composition_url && (
                    <a
                      href={it.composition_url}
                      target="_blank"
                      rel="noreferrer"
                      className="bp-btn bp-btn--secondary"
                      title="Voir la capture"
                    >
                      Voir
                    </a>
                  )}
                  {it.composition_url && (
                    <button
                      onClick={() => onDownload(it)}
                      className="bp-btn bp-btn--secondary"
                      title="Télécharger"
                      type="button"
                    >
                      Télécharger
                    </button>
                  )}
                  <button
                    onClick={() => onOpenEdit(it)}
                    className="bp-btn bp-btn--primary"
                    title="Voir et Éditer"
                  >
                    Voir et Éditer
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Modal */}
      {open && current && (
        <div className="bp-backdrop" onClick={onClose}>
          <div className="bp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bp-header" style={{ marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: "clamp(16px, 2vw, 18px)" }}>Éditer la création</h3>
              <button onClick={onClose} className="bp-btn bp-btn--secondary">Fermer</button>
            </div>

            <div className="bp-toolbar">
              <button onClick={() => applyExec("bold")} className="bp-btn bp-btn--secondary">Gras</button>
              <button onClick={() => applyExec("italic")} className="bp-btn bp-btn--secondary">Italique</button>
              <button onClick={() => applyExec("underline")} className="bp-btn bp-btn--secondary">Souligné</button>

              <select value={fontFamily} onChange={onFontChange} className="bp-btn bp-btn--secondary" style={{ minHeight: 44 }}>
                <option value="Arial">Arial</option>
                <option value="Verdana">Verdana</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Georgia">Georgia</option>
              </select>

              <label style={{ fontSize: 14 }}>Couleur</label>
              <input type="color" value={color} onChange={onColorChange} style={{ width: 44, height: 44, border: "none" }} />
            </div>

            <textarea
              value={message.replace(/<br\s*\/?>(?=\s|$)/gi, "\n")}
              onChange={onTextareaChange}
              placeholder="Écrire un message"
              className="bp-input"
              rows={3}
            />

            <div ref={containerRef} className="bp-canvas">
              {current.original_url ? (
                <img
                  ref={imgRef}
                  alt="original"
                  src={current.original_url}
                  crossOrigin="anonymous"
                  onLoad={computeTailSide}
                  className="bp-avatar"
                />
              ) : (
                <div style={{ color: "#777" }}>(Pas d’image d’origine enregistrée)</div>
              )}

              <div
                ref={bubbleBoxRef}
                className="bp-bubble"
                style={bubbleStyle}
              >
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={onBubbleInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      applyExec("insertHTML", "<br><br>");
                    }
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: message }} />
                </div>

                {(() => {
                  const { top, under } = getTailLayers();
                  return (
                    <>
                      <span aria-hidden style={under} />
                      <span aria-hidden style={top} />
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="bp-actions" style={{ justifyContent: "flex-end", marginTop: 12 }}>
              <button onClick={onClose} className="bp-btn bp-btn--secondary">Annuler</button>
              <button onClick={onCaptureDownload} className="bp-btn bp-btn--secondary" disabled={capturing}>
                {capturing ? "Capture…" : "Capturer (PNG)"}
              </button>
              <button onClick={onSave} className="bp-btn bp-btn--primary" disabled={saving || capturing}>
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
